class NavDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = null;
    this.shell = null;
    this.secondary = null;
    this.aside = null;
    this.media = null;
    this.indicator = null;
    this.trigger = null;
    this.activeExpandId = null;
    this.closeTimeout = null;
    this.expandCloseTimeout = null;
    this.onExpandTransitionEnd = null;
    this.onDocumentKeyUp = this.onDocumentKeyUp.bind(this);
    this.syncHeaderBottom = this.syncHeaderBottom.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('.nav-drawer__panel');
    this.shell = this.querySelector('.nav-drawer__shell');
    this.secondary = this.querySelector('[data-nav-secondary]');
    this.aside = this.querySelector('[data-nav-aside]');
    this.media = this.querySelector('[data-nav-media]');
    this.indicator = this.querySelector('[data-nav-tab-indicator]');
    this.trigger = document.querySelector('[data-nav-drawer-trigger]');

    if (!this.panel || !this.trigger || !this.shell) return;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.querySelectorAll('[data-nav-drawer-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });
    this.querySelectorAll('[data-nav-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.setActiveTab(tab.dataset.navTab));
    });
    this.querySelectorAll('[data-nav-expand]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleExpand(btn.dataset.navExpand));
    });
    this.querySelectorAll('[data-nav-expand-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.collapseExpand());
    });

    window.addEventListener('resize', this.syncHeaderBottom);
    requestAnimationFrame(() => {
      this.syncHeaderBottom();
      this.updateTabIndicator();
      this.syncMediaVisibility();
    });
  }

  isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  isOpen() {
    return this.panel.classList.contains('is-open');
  }

  onTriggerClick(event) {
    event.preventDefault();
    if (this.isOpen()) this.close();
    else this.open();
  }

  onDocumentKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  syncHeaderBottom() {
    const headerBar =
      document.querySelector('#shopify-section-header .header') ||
      document.querySelector('.header-wrapper > .header') ||
      document.getElementById('shopify-section-header');
    if (!headerBar) return;
    const bottom = Math.round(headerBar.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
  }

  open() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.syncHeaderBottom();
    this.panel.hidden = false;
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.classList.add('is-visible');
    void this.shell.offsetWidth;

    requestAnimationFrame(() => {
      this.syncHeaderBottom();
      this.panel.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nav-drawer-open');
      const header = document.getElementById('shopify-section-header');
      if (header) header.classList.add('menu-open');
      this.updateTabIndicator();
      document.addEventListener('keyup', this.onDocumentKeyUp);
      window.addEventListener('scroll', this.syncHeaderBottom, { passive: true });
    });
  }

  close() {
    if (!this.isOpen() && !this.panel.classList.contains('is-visible')) return;

    this.collapseExpand();
    this.panel.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('nav-drawer-open');
    const header = document.getElementById('shopify-section-header');
    if (header) header.classList.remove('menu-open');
    document.removeEventListener('keyup', this.onDocumentKeyUp);
    window.removeEventListener('scroll', this.syncHeaderBottom);

    const finishClose = () => {
      if (this.isOpen()) return;
      this.panel.classList.remove('is-visible');
      this.panel.hidden = true;
      this.panel.setAttribute('aria-hidden', 'true');
      this.shell.removeEventListener('transitionend', onEnd);
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    };

    const onEnd = (event) => {
      if (event.target !== this.shell || event.propertyName !== 'transform') return;
      finishClose();
    };

    this.shell.addEventListener('transitionend', onEnd);
    this.closeTimeout = setTimeout(finishClose, 300);
  }

  setActiveTab(handle) {
    this.collapseExpand();

    this.querySelectorAll('[data-nav-tab]').forEach((tab) => {
      const active = tab.dataset.navTab === handle;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.querySelectorAll('[data-nav-panel]').forEach((panel) => {
      const active = panel.dataset.navPanel === handle;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    this.querySelectorAll('[data-nav-media-panel]').forEach((panel) => {
      const active = panel.dataset.navMediaPanel === handle;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    this.syncMediaVisibility(handle);
    this.updateTabIndicator();
  }

  syncMediaVisibility(handle) {
    if (!this.media && !this.aside) return;

    const activeTab = this.querySelector('.nav-drawer__tab.is-active');
    const activeHandle = handle || (activeTab && activeTab.dataset.navTab) || this.dataset.defaultTab;
    const hasPanel = Boolean(
      activeHandle && this.querySelector(`[data-nav-media-panel="${activeHandle}"]`)
    );

    if (this.media) this.media.classList.toggle('is-empty', !hasPanel);
  }

  updateTabIndicator() {
    if (!this.indicator) return;
    const activeTab = this.querySelector('.nav-drawer__tab.is-active');
    const tabs = this.querySelector('.nav-drawer__tabs');
    if (!activeTab || !tabs) return;

    const tabsRect = tabs.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    this.indicator.style.width = `${Math.round(tabRect.width)}px`;
    this.indicator.style.transform = `translateX(${tabRect.left - tabsRect.left}px)`;
  }

  clearExpandTimers() {
    if (this.expandCloseTimeout) {
      clearTimeout(this.expandCloseTimeout);
      this.expandCloseTimeout = null;
    }
    if (this.onExpandTransitionEnd) {
      const target = this.aside || this.secondary;
      if (target) target.removeEventListener('transitionend', this.onExpandTransitionEnd);
      this.onExpandTransitionEnd = null;
    }
  }

  toggleExpand(expandId) {
    if (this.activeExpandId === expandId) this.collapseExpand();
    else this.expand(expandId);
  }

  setExpandPanels(expandId) {
    this.querySelectorAll('[data-nav-expand]').forEach((btn) => {
      const active = Boolean(expandId) && btn.dataset.navExpand === expandId;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-expanded', active ? 'true' : 'false');
    });

    this.querySelectorAll('[data-nav-expand-panel]').forEach((panel) => {
      const active = Boolean(expandId) && panel.dataset.navExpandPanel === expandId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  expand(expandId) {
    this.clearExpandTimers();
    this.activeExpandId = expandId;
    this.setExpandPanels(expandId);

    if (this.secondary) this.secondary.setAttribute('aria-hidden', 'false');

    if (!this.panel.classList.contains('is-expanded')) {
      void (this.aside || this.secondary || this.panel).offsetWidth;
      requestAnimationFrame(() => this.panel.classList.add('is-expanded'));
      return;
    }

    this.panel.classList.add('is-expanded');
  }

  collapseExpand() {
    if (!this.panel.classList.contains('is-expanded') && !this.activeExpandId) return;

    this.clearExpandTimers();
    this.activeExpandId = null;

    this.querySelectorAll('[data-nav-expand]').forEach((btn) => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-expanded', 'false');
    });

    if (!this.secondary) {
      this.panel.classList.remove('is-expanded');
      this.setExpandPanels(null);
      return;
    }

    this.secondary.setAttribute('aria-hidden', 'true');

    const collapseTarget = this.aside || this.secondary;
    const waitForSlide = !this.isDesktop() && Boolean(this.aside);

    const finishCollapse = (event) => {
      if (event) {
        if (event.target !== collapseTarget) return;
        if (waitForSlide && event.propertyName !== 'transform') return;
        if (!waitForSlide && event.propertyName && event.propertyName !== 'opacity') {
          return;
        }
      }
      if (this.panel.classList.contains('is-expanded')) return;
      this.setExpandPanels(null);
      this.clearExpandTimers();
    };

    this.panel.classList.remove('is-expanded');
    this.onExpandTransitionEnd = finishCollapse;
    collapseTarget.addEventListener('transitionend', finishCollapse);
    this.expandCloseTimeout = setTimeout(() => finishCollapse(), 300);
  }
}

customElements.define('nav-drawer', NavDrawer);
